import React, { useEffect, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

const isValidStripePublicKey = (key: string) => {
  return key.startsWith('pk_test_') || key.startsWith('pk_live_');
};

// Initialize Stripe with explicit error handling
const initializeStripe = async () => {
  const key = process.env.REACT_APP_STRIPE_PUBLIC_KEY?.trim();
  
  // More detailed debugging
  console.log('Stripe Initialization Debug:', {
    hasKey: !!key,
    keyLength: key?.length,
    keyStart: key?.substring(0, 10) + '...',
    source: 'environment',
    envVars: {
      NODE_ENV: process.env.NODE_ENV,
      PUBLIC_URL: process.env.PUBLIC_URL,
      hasStripeKey: !!process.env.REACT_APP_STRIPE_PUBLIC_KEY
    }
  });
  
  if (!key) {
    console.error('Stripe key is missing. Check that:',
      '\n1. Your .env file exists and contains REACT_APP_STRIPE_PUBLIC_KEY',
      '\n2. The key format is correct (starts with pk_test_ or pk_live_)',
      '\n3. The development server was restarted after adding the key');
    throw new Error('Stripe public key is not set in environment variables');
  }
  
  if (!isValidStripePublicKey(key)) {
    console.error('Invalid key format:', key.substring(0, 7) + '...');
    throw new Error('Invalid Stripe public key format. Must start with pk_test_ or pk_live_');
  }

  console.log('Attempting to load Stripe with valid key format');
  // Note: apiVersion is now managed server-side only in Stripe.js v8+
  return loadStripe(key);
};

const stripePromise = initializeStripe();

interface StripeProviderProps {
  children: React.ReactNode;
}

const StripeProvider: React.FC<StripeProviderProps> = ({ children }) => {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Verify Stripe initialization
    stripePromise.then(stripe => {
      if (!stripe) {
        console.error('Failed to initialize Stripe');
        setError('Failed to initialize Stripe');
      } else {
        console.log('Stripe initialized successfully');
        // Test a basic Stripe API call
        stripe.registerAppInfo({
          name: 'WrapPractice',
          version: '1.0.0',
        });
      }
    }).catch(err => {
      console.error('Stripe initialization error:', err);
      setError(err.message);
    });
  }, []);

  if (error) {
    return (
      <div className="text-red-500 p-4 bg-red-100 rounded">
        <h3 className="font-bold">Stripe Error</h3>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise} options={{
      mode: 'setup',
      currency: 'usd',
      appearance: {
        theme: 'night',
        variables: {
          colorPrimary: '#0066cc',
        },
      },
    }}>
      {children}
    </Elements>
  );
};

export default StripeProvider; 